import { useEffect, useRef, useCallback, useMemo } from "react";
import { FormWidgetEnv } from "@xapp/stentor-form-widget-channel";

type AutoOpenConfig = FormWidgetEnv["autoOpen"];

export interface UseAutoOpenTriggersOptions {
    config?: AutoOpenConfig;
    isActive: boolean;
    onOpen: () => void;
    hasInteracted: boolean;
}

/** Default sessionStorage key for tracking dismissed state */
export const DEFAULT_SESSION_KEY = "xafw-auto-open-dismissed";
const DEFAULT_EXIT_INTENT_THRESHOLD = 50;
const MIN_DELAY = 0;
const MAX_DELAY = 300000; // 5 minutes
const MIN_SCROLL_PERCENT = 0;
const MAX_SCROLL_PERCENT = 100;

/**
 * Validates and clamps a delay/inactivity value to safe bounds
 */
function validateDelay(value: number | undefined): number | undefined {
    if (typeof value !== "number" || value < MIN_DELAY) return undefined;
    return Math.min(value, MAX_DELAY);
}

/**
 * Validates and clamps scroll percentage to 0-100
 */
function validateScrollPercent(value: number | undefined): number | undefined {
    if (typeof value !== "number") return undefined;
    return Math.max(MIN_SCROLL_PERCENT, Math.min(value, MAX_SCROLL_PERCENT));
}

/**
 * Validates exit intent threshold (must be positive)
 */
function validateThreshold(value: number | undefined): number {
    if (typeof value !== "number" || value < 0) return DEFAULT_EXIT_INTENT_THRESHOLD;
    return value;
}

/**
 * Checks if the form was already dismissed this session
 */
function isDismissed(sessionKey: string): boolean {
    try {
        return sessionStorage.getItem(sessionKey) === "true";
    } catch {
        // Private browsing or sessionStorage not available
        return false;
    }
}

/**
 * Returns true if any behavior-based auto-open trigger (delay, scrollPercent,
 * exitIntent, inactivity) is configured.
 *
 * Used to distinguish "open immediately" (`enabled` alone) from "open once a
 * trigger condition is met" (`enabled` + `triggers`).
 */
export function hasAutoOpenTriggers(config?: AutoOpenConfig): boolean {
    const triggers = config?.triggers;
    // Compare the *validated* values, not the raw config. The hook below arms its listeners
    // off the validated values too, so reading the raw ones here lets the two disagree: a
    // config like `{ delay: -1 }` would look like a configured trigger (so the form waits
    // instead of opening immediately) while the hook discards it (so no trigger ever fires)
    // -- and the form would silently never open at all.
    return (
        validateDelay(triggers?.delay) !== undefined ||
        validateScrollPercent(triggers?.scrollPercent) !== undefined ||
        !!triggers?.exitIntent ||
        validateDelay(triggers?.inactivity) !== undefined
    );
}

/**
 * Checks if current URL matches any of the provided patterns
 */
function matchesUrlPatterns(patterns?: string[]): boolean {
    if (!patterns || patterns.length === 0) {
        return true;
    }
    const currentUrl = window.location.href;
    return patterns.some((pattern) => currentUrl.includes(pattern));
}

/**
 * Checks if viewport width meets minimum requirement
 */
function meetsMinimumWidth(minimumWidth?: number): boolean {
    if (typeof minimumWidth !== "number") {
        return true;
    }
    return window.innerWidth >= minimumWidth;
}

/**
 * Checks if device supports touch (for disabling exit intent on touch devices)
 */
function isTouchDevice(): boolean {
    return "ontouchstart" in window || navigator.maxTouchPoints > 0;
}

/**
 * Hook for auto-opening the form widget based on configurable triggers.
 *
 * Supports:
 * - delay: Timer-based trigger (milliseconds)
 * - scrollPercent: Scroll-based trigger (0-100)
 * - exitIntent: Mouse leaving viewport trigger
 * - inactivity: User idle trigger (milliseconds)
 *
 * Modes:
 * - 'any' (default): First trigger that fires opens the form
 * - 'all': All configured triggers must fire before opening
 */
export function useAutoOpenTriggers({
    config,
    isActive,
    onOpen,
    hasInteracted,
}: UseAutoOpenTriggersOptions): void {
    const triggersRef = useRef<Set<string>>(new Set());
    const hasOpenedRef = useRef(false);
    const inactivityTimerRef = useRef<NodeJS.Timeout | null>(null);

    const sessionKey = config?.sessionKey || DEFAULT_SESSION_KEY;
    const mode = config?.mode || "any";
    const triggers = config?.triggers;

    // Extract and validate primitive values to use as stable dependencies
    const delay = validateDelay(triggers?.delay);
    const scrollPercent = validateScrollPercent(triggers?.scrollPercent);
    const exitIntent = triggers?.exitIntent;
    const exitIntentThreshold = validateThreshold(triggers?.exitIntentThreshold);
    const inactivity = validateDelay(triggers?.inactivity);
    const patterns = config?.patterns;
    const minimumWidth = config?.minimumWidth;

    // Serialize patterns array to create a stable dependency
    // This prevents effect re-runs when array reference changes but contents are identical
    const patternsKey = useMemo(() => JSON.stringify(patterns ?? []), [patterns]);

    // Calculate which triggers are configured
    const configuredTriggers = useRef<string[]>([]);

    // Get list of configured triggers
    const getConfiguredTriggers = useCallback((): string[] => {
        const list: string[] = [];
        if (triggers?.delay !== undefined) list.push("delay");
        if (triggers?.scrollPercent !== undefined) list.push("scrollPercent");
        if (triggers?.exitIntent) list.push("exitIntent");
        if (triggers?.inactivity !== undefined) list.push("inactivity");
        return list;
    }, [triggers?.delay, triggers?.scrollPercent, triggers?.exitIntent, triggers?.inactivity]);

    // Check if we should open based on mode and fired triggers
    const checkAndOpen = useCallback(
        (triggerName: string) => {
            if (hasOpenedRef.current) return;

            triggersRef.current.add(triggerName);

            if (mode === "any") {
                // Open on first trigger
                hasOpenedRef.current = true;
                onOpen();
            } else if (mode === "all") {
                // Check if all configured triggers have fired
                const configured = configuredTriggers.current;
                const allFired = configured.every((t) => triggersRef.current.has(t));
                if (allFired) {
                    hasOpenedRef.current = true;
                    onOpen();
                }
            }
        },
        [mode, onOpen]
    );

    useEffect(() => {
        // Reset state when config changes
        triggersRef.current = new Set();
        hasOpenedRef.current = false;
        configuredTriggers.current = getConfiguredTriggers();
    }, [getConfiguredTriggers]);

    useEffect(() => {
        // Guard conditions - skip if any are true
        const hasTriggers =
            delay !== undefined ||
            scrollPercent !== undefined ||
            exitIntent ||
            inactivity !== undefined;
        if (!hasTriggers) return;
        if (isActive) return;
        if (hasInteracted) return;
        if (isDismissed(sessionKey)) return;
        if (!matchesUrlPatterns(patterns)) return;
        if (!meetsMinimumWidth(minimumWidth)) return;

        const timers: NodeJS.Timeout[] = [];
        const listeners: Array<{
            target: EventTarget;
            event: string;
            handler: EventListener;
            options?: AddEventListenerOptions;
        }> = [];

        const addListener = (
            target: EventTarget,
            event: string,
            handler: EventListener,
            options?: AddEventListenerOptions
        ) => {
            target.addEventListener(event, handler, options);
            listeners.push({ target, event, handler, options });
        };

        // Delay trigger
        if (typeof delay === "number" && delay > 0) {
            const timer = setTimeout(() => {
                checkAndOpen("delay");
            }, delay);
            timers.push(timer);
        }

        // Scroll trigger (throttled with requestAnimationFrame for performance)
        if (typeof scrollPercent === "number") {
            const targetPercent = scrollPercent;
            let scrollTicking = false;

            const checkScrollPercent = () => {
                const scrollTop = window.scrollY || document.documentElement.scrollTop;
                const docHeight = document.documentElement.scrollHeight;
                const windowHeight = window.innerHeight;
                const scrollableHeight = docHeight - windowHeight;

                if (scrollableHeight <= 0) return;

                const currentPercent = (scrollTop / scrollableHeight) * 100;
                if (currentPercent >= targetPercent) {
                    checkAndOpen("scrollPercent");
                }
                scrollTicking = false;
            };

            const handleScroll = () => {
                if (!scrollTicking) {
                    scrollTicking = true;
                    // Use requestAnimationFrame for throttling if available, otherwise execute directly
                    if (typeof requestAnimationFrame === "function") {
                        requestAnimationFrame(checkScrollPercent);
                    } else {
                        checkScrollPercent();
                    }
                }
            };

            addListener(window, "scroll", handleScroll, { passive: true });
            // Check immediately in case already scrolled
            checkScrollPercent();
        }

        // Exit intent trigger (skip on touch devices)
        if (exitIntent && !isTouchDevice()) {
            const threshold = exitIntentThreshold ?? DEFAULT_EXIT_INTENT_THRESHOLD;

            const handleMouseOut = (e: Event) => {
                const mouseEvent = e as MouseEvent;
                // Only trigger when mouse leaves through the top
                if (mouseEvent.clientY <= threshold) {
                    checkAndOpen("exitIntent");
                }
            };

            addListener(document, "mouseout", handleMouseOut);
        }

        // Inactivity trigger (uses separate ref to avoid array manipulation issues)
        if (typeof inactivity === "number" && inactivity > 0) {
            const resetInactivityTimer = () => {
                if (inactivityTimerRef.current) {
                    clearTimeout(inactivityTimerRef.current);
                }
                inactivityTimerRef.current = setTimeout(() => {
                    checkAndOpen("inactivity");
                }, inactivity);
            };

            // Start the timer
            resetInactivityTimer();

            // Reset on user activity (passive listeners for better performance)
            const activityEvents = ["mousemove", "keydown", "scroll", "click", "touchstart"];
            activityEvents.forEach((event) => {
                addListener(document, event, resetInactivityTimer, { passive: true });
            });
        }

        // Cleanup
        return () => {
            timers.forEach((timer) => clearTimeout(timer));
            if (inactivityTimerRef.current) {
                clearTimeout(inactivityTimerRef.current);
                inactivityTimerRef.current = null;
            }
            listeners.forEach(({ target, event, handler, options }) => {
                target.removeEventListener(event, handler, options);
            });
        };
    }, [
        // Use primitive values as dependencies to avoid re-running on object reference changes
        delay,
        scrollPercent,
        exitIntent,
        exitIntentThreshold,
        inactivity,
        patternsKey, // Serialized patterns for stable comparison
        minimumWidth,
        isActive,
        hasInteracted,
        sessionKey,
        checkAndOpen,
    ]);
}
